/**
 * Static data + pure helpers for the Annual DOT Inspection tab.
 *
 * Mirrors docs/dot-annual-inspection.html (49 CFR §396.17 / Appendix G checklist).
 * Item labels stay as plain English strings (fixed regulatory wording), unlike
 * section titles and every other UI label, which go through translate().
 */

export const VEHICLE_TYPES = [
    {value: 'box', labelKey: 'text.dot_vehicle_box_truck'},
    {value: 'semi', labelKey: 'text.dot_vehicle_semi_truck'},
    {value: 'trailer', labelKey: 'text.dot_vehicle_trailer'},
    {value: 'standard', labelKey: 'text.dot_vehicle_others'}
];

export const STATUSES = [
    {key: 'ok', labelKey: 'text.dot_status_ok', dotClass: 'bg-emerald-500', activeClass: 'bg-emerald-100 text-emerald-800 ring-emerald-500'},
    {key: 'def', labelKey: 'text.dot_status_defect', dotClass: 'bg-red-500', activeClass: 'bg-red-100 text-red-800 ring-red-500'},
    {key: 'rep', labelKey: 'text.dot_status_repaired', dotClass: 'bg-amber-500', activeClass: 'bg-amber-100 text-amber-800 ring-amber-500'},
    {key: 'na', labelKey: 'text.dot_status_na', dotClass: 'bg-tm-gray-400', activeClass: 'bg-tm-gray-200 text-tm-gray-700 ring-tm-gray-400'}
];

export const RESULT_BADGE_CLASS = {
    pass: "bg-green-100 text-green-800",
    fail: "bg-red-100 text-red-800",
    pending: "bg-tm-gray-100 text-tm-gray-700"
};

const T_ALL = ['box', 'semi', 'trailer'];
const T_TRUCK = ['box', 'semi'];
const T_SEMI = ['semi'];
const T_TRAILER = ['trailer'];
const T_BOX = ['box'];
const T_BOX_TRAILER = ['box', 'trailer'];
const NONE = [];

export const SECTIONS = [
    {
        id: 'brakes',
        titleKey: 'card_header.dot_inspection_brakes',
        extra: 'brakes',
        items: [
            {id: 'brake_service', label: 'Service brakes — condition, adjustment, function', for: T_ALL},
            {id: 'brake_parking', label: 'Parking brake system', for: T_ALL},
            {id: 'brake_drums_rotors', label: 'Brake drums or rotors', for: T_ALL},
            {id: 'brake_hose', label: 'Brake hose', for: T_ALL},
            {id: 'brake_tubing', label: 'Brake tubing', for: T_ALL},
            {id: 'brake_linings_pads', label: 'Brake linings / pads — thickness within limit', for: T_ALL},
            {id: 'brake_low_pressure_warning', label: 'Low-pressure warning device', for: T_TRUCK},
            {id: 'brake_tractor_protection_valve', label: 'Tractor protection valve', for: T_SEMI},
            {id: 'brake_air_compressor', label: 'Air compressor', for: T_TRUCK},
            {id: 'brake_electric', label: 'Electric brakes (if equipped)', for: T_TRAILER},
            {id: 'brake_hydraulic', label: 'Hydraulic brakes (if equipped)', for: T_BOX},
            {id: 'brake_vacuum_system', label: 'Vacuum system (if equipped)', for: T_BOX},
            {id: 'brake_air_hydraulic_reservoir', label: 'Air / hydraulic reservoir & check valve', for: T_ALL},
            {id: 'brake_air_loss_rate', label: 'Air loss / leakage rate within limits', for: T_ALL}
        ]
    },
    {
        id: 'coupling',
        titleKey: 'card_header.dot_inspection_coupling',
        items: [
            {id: 'coupling_fifth_wheels', label: 'Fifth wheels', for: T_SEMI},
            {id: 'coupling_kingpin', label: 'Kingpin / upper coupler assembly', for: T_TRAILER},
            {id: 'coupling_pintle_hooks', label: 'Pintle hooks (if equipped)', for: T_ALL},
            {id: 'coupling_drawbar_eye', label: 'Drawbar / towbar eye (if equipped)', for: T_ALL},
            {id: 'coupling_drawbar_tongue', label: 'Drawbar / towbar tongue (if equipped)', for: T_ALL},
            {id: 'coupling_safety_devices', label: 'Safety devices — chains / hooks (if equipped)', for: T_ALL},
            {id: 'coupling_saddle_mounts', label: 'Saddle-mounts (if equipped)', for: T_ALL},
            {id: 'coupling_landing_gear', label: 'Landing gear — condition & crank', for: T_TRAILER}
        ]
    },
    {
        id: 'exhaust',
        titleKey: 'card_header.dot_inspection_exhaust',
        items: [
            {id: 'exhaust_no_leak_forward', label: 'No leak forward of / directly below driver or sleeper', for: T_TRUCK},
            {id: 'exhaust_no_burn_char', label: 'Not located to burn/char wiring, fuel, or combustible parts', for: T_TRUCK},
            {id: 'exhaust_mounting_discharge', label: 'Mounting & discharge location', for: T_TRUCK},
            {id: 'exhaust_bus_discharge', label: 'Bus exhaust discharge within standards', for: NONE}
        ]
    },
    {
        id: 'fuel',
        titleKey: 'card_header.dot_inspection_fuel',
        items: [
            {id: 'fuel_no_visible_leak', label: 'No visible leak', for: T_TRUCK},
            {id: 'fuel_filler_cap', label: 'Fuel tank filler cap present', for: T_TRUCK},
            {id: 'fuel_tank_attached', label: 'Fuel tank securely attached', for: T_TRUCK},
            {id: 'fuel_lines_protected', label: 'Lines protected & secured', for: T_TRUCK}
        ]
    },
    {
        id: 'lighting',
        titleKey: 'card_header.dot_inspection_lighting',
        items: [
            {id: 'lighting_headlamps', label: 'Headlamps (high/low)', for: T_TRUCK},
            {id: 'lighting_tail_stop_lamps', label: 'Tail lamps & stop lamps', for: T_ALL},
            {id: 'lighting_turn_signals', label: 'Turn signals & hazard flashers', for: T_ALL},
            {id: 'lighting_clearance_marker', label: 'Clearance, side-marker & identification lamps', for: T_ALL},
            {id: 'lighting_reflectors', label: 'Reflectors / conspicuity tape', for: T_ALL}
        ]
    },
    {
        id: 'safe_loading',
        titleKey: 'card_header.dot_inspection_safe_loading',
        items: [
            {id: 'loading_secured', label: 'Load / dunnage / spare tire secured — nothing can fall to roadway', for: T_ALL},
            {id: 'loading_shifting_protection', label: 'Protection against shifting cargo', for: T_ALL}
        ]
    },
    {
        id: 'steering',
        titleKey: 'card_header.dot_inspection_steering',
        items: [
            {id: 'steering_wheel_free_play', label: 'Steering wheel free play', for: T_TRUCK},
            {id: 'steering_column', label: 'Steering column', for: T_TRUCK},
            {id: 'steering_front_axle_beam', label: 'Front axle beam & other steering components', for: T_TRUCK},
            {id: 'steering_gear_box', label: 'Steering gear box', for: T_TRUCK},
            {id: 'steering_pitman_arm', label: 'Pitman arm', for: T_TRUCK},
            {id: 'steering_power_steering', label: 'Power steering', for: T_TRUCK},
            {id: 'steering_ball_socket_joints', label: 'Ball & socket joints', for: T_TRUCK},
            {id: 'steering_tie_rods_drag_links', label: 'Tie rods & drag links', for: T_TRUCK},
            {id: 'steering_nuts_fasteners', label: 'Steering component nuts/fasteners', for: T_TRUCK},
            {id: 'steering_overall_condition', label: 'Steering system — overall condition', for: T_TRUCK}
        ]
    },
    {
        id: 'suspension',
        titleKey: 'card_header.dot_inspection_suspension',
        items: [
            {id: 'suspension_u_bolts', label: 'U-bolts, spring hangers & axle positioning parts', for: T_ALL},
            {id: 'suspension_spring_assembly', label: 'Spring assembly', for: T_ALL},
            {id: 'suspension_torque_radius_tracking', label: 'Torque/radius/tracking components', for: T_ALL},
            {id: 'suspension_springs_air_bags', label: 'Springs/air bags — condition', for: T_ALL},
            {id: 'suspension_shock_absorbers', label: 'Shock absorbers/attachments', for: T_ALL}
        ]
    },
    {
        id: 'frame',
        titleKey: 'card_header.dot_inspection_frame',
        items: [
            {id: 'frame_members', label: 'Frame members — cracks, breaks, sagging', for: T_ALL},
            {id: 'frame_tire_wheel_clearance', label: 'Tire & wheel clearance', for: T_ALL},
            {id: 'frame_adjustable_axle', label: 'Adjustable axle assemblies/sliding subframes', for: T_TRAILER}
        ]
    },
    {
        id: 'tires',
        titleKey: 'card_header.dot_inspection_tires',
        extra: 'tires',
        items: [
            {id: 'tires_steering_axle', label: 'Tires on any steering axle — condition', for: T_TRUCK},
            {id: 'tires_all_other', label: 'All other tires — condition', for: T_ALL},
            {id: 'tires_no_cuts_bulges', label: 'No cuts/bulges/exposed fabric or cord', for: T_ALL},
            {id: 'tires_load_rating', label: 'Correct load rating/not mismatched', for: T_ALL}
        ]
    },
    {
        id: 'wheels_rims',
        titleKey: 'card_header.dot_inspection_wheels_rims',
        items: [
            {id: 'wheels_lock_side_ring', label: 'Lock or side ring', for: T_ALL},
            {id: 'wheels_cracks_damage', label: 'Wheels & rims — cracks, damage', for: T_ALL},
            {id: 'wheels_fasteners', label: 'Fasteners/lug nuts — all present & tight', for: T_ALL},
            {id: 'wheels_welds', label: 'Welds', for: T_ALL}
        ]
    },
    {
        id: 'windshield',
        titleKey: 'card_header.dot_inspection_windshield',
        items: [
            {id: 'windshield_cracks', label: 'Windshield — cracks/discoloration within limits (393.60)', for: T_TRUCK},
            {id: 'windshield_wipers', label: 'Wipers operational', for: T_TRUCK}
        ]
    },
    {
        id: 'cargo_body',
        titleKey: 'card_header.dot_inspection_cargo_body',
        items: [
            {id: 'cargo_body_mounting', label: 'Body/box mounting & structure', for: T_BOX},
            {id: 'cargo_doors_hinges', label: 'Doors, hinges & latches', for: T_BOX_TRAILER},
            {id: 'cargo_floor_condition', label: 'Floor condition', for: T_BOX_TRAILER}
        ]
    },
    {
        id: 'underride_guard',
        titleKey: 'card_header.dot_inspection_underride_guard',
        items: [
            {id: 'guard_present', label: 'Guard present, correct dimensions', for: T_TRAILER},
            {id: 'guard_attached', label: 'Securely attached, no corrosion/damage', for: T_TRAILER}
        ]
    }
];

const TIRE_POSITIONS = {
    semi: ['A1-L', 'A1-R', 'A2-LI', 'A2-LO', 'A2-RI', 'A2-RO', 'A3-LI', 'A3-LO', 'A3-RI', 'A3-RO'],
    box: ['A1-L', 'A1-R', 'A2-LI', 'A2-LO', 'A2-RI', 'A2-RO'],
    trailer: ['A1-LI', 'A1-LO', 'A1-RI', 'A1-RO', 'A2-LI', 'A2-LO', 'A2-RI', 'A2-RO'],
    standard: ['A1-L', 'A1-R', 'A2-LI', 'A2-LO', 'A2-RI', 'A2-RO']
};

const BRAKE_POSITIONS = {
    semi: ['A1-L', 'A1-R', 'A2-L', 'A2-R', 'A3-L', 'A3-R'],
    box: ['A1-L', 'A1-R', 'A2-L', 'A2-R'],
    trailer: ['A1-L', 'A1-R', 'A2-L', 'A2-R'],
    standard: ['A1-L', 'A1-R', 'A2-L', 'A2-R']
};

// Client-only row identity for React keys, never sent to the API (the Save
// handler strips it before building the payload). A plain counter keeps this
// module import-free and deterministic within a session, mirroring the _uid
// pattern in vehicle-inspections-view/dvir-template-utils.js.
let nextRowUidNumber = 1;

function nextRowUid(prefix) {
    return prefix + '-' + (nextRowUidNumber++);
}

export function emptyTireRow() {
    return {_uid: nextRowUid('tire'), pos: '', tread: '', psi: ''};
}

export function emptyBrakeRow() {
    return {_uid: nextRowUid('brake'), pos: '', stroke: '', pad: ''};
}

// Rows loaded from the API keep their PK (WorkOrderDotInspectionTireRowID /
// WorkOrderDotInspectionBrakeRowID) so the backend can update them in place
// on save; `_uid` is re-added for React keys only.
export function hydrateTireRows(rows = []) {
    return rows.map((row) => Object.assign(emptyTireRow(), {
        WorkOrderDotInspectionTireRowID: row.WorkOrderDotInspectionTireRowID,
        pos: row.pos ?? '',
        tread: row.tread ?? '',
        psi: row.psi ?? ''
    }));
}

export function hydrateBrakeRows(rows = []) {
    return rows.map((row) => Object.assign(emptyBrakeRow(), {
        WorkOrderDotInspectionBrakeRowID: row.WorkOrderDotInspectionBrakeRowID,
        pos: row.pos ?? '',
        stroke: row.stroke ?? '',
        pad: row.pad ?? ''
    }));
}

export function getTirePreset(vehicleType) {
    const positions = TIRE_POSITIONS[vehicleType] ?? TIRE_POSITIONS.box;

    return positions.map((pos) => Object.assign(emptyTireRow(), {pos}));
}

export function getBrakePreset(vehicleType) {
    const positions = BRAKE_POSITIONS[vehicleType] ?? BRAKE_POSITIONS.box;

    return positions.map((pos) => Object.assign(emptyBrakeRow(), {pos}));
}

// Odometer only makes sense on a powered unit; trailers don't have one.
// "Others" defaults to visible, matching the original's power-unit fallback.
export function isOdometerVisible(vehicleType) {
    return vehicleType !== 'trailer';
}

export function appliesToVehicleType(item, vehicleType) {
    // "Others" shows every item regardless of applicability tag, same as the
    // reference HTML's appliesItem() short-circuit for vehicleType==="standard".
    if (vehicleType === 'standard') {
        return true;
    }

    return item.for.includes(vehicleType);
}

export function getVisibleSections(vehicleType) {
    return SECTIONS
        .map((section) => Object.assign({}, section, {
            items: section.items.filter((item) => appliesToVehicleType(item, vehicleType))
        }))
        .filter((section) => section.items.length > 0);
}

export function computeInspectionResult(visibleSections, answers) {
    let total = 0;
    let defects = 0;
    let unmarked = 0;

    visibleSections.forEach((section) => {
        section.items.forEach((item) => {
            total++;

            const status = answers[item.id];

            if (status === 'def') {
                defects++;
            }

            if (!status) {
                unmarked++;
            }
        });
    });

    let result = null;

    if (total > 0) {
        if (defects > 0) {
            result = 'fail';
        } else if (unmarked === 0) {
            result = 'pass';
        }
    }

    return {defects, unmarked, total, result};
}

// Only marked items are sent -- the API rejects null statuses, and an item
// left out of the payload is treated as unmarked (deleted) on update.
export function buildAnswersPayload(visibleSections, answers) {
    const payload = {};

    visibleSections.forEach((section) => {
        section.items.forEach((item) => {
            if (answers[item.id]) {
                payload[item.id] = answers[item.id];
            }
        });
    });

    return payload;
}

export function toggleItemStatus(answers, itemId, statusKey) {
    const next = Object.assign({}, answers);

    if (next[itemId] === statusKey) {
        delete next[itemId];
    } else {
        next[itemId] = statusKey;
    }

    return next;
}

export function applyBulkStatus(answers, itemIds, statusKey) {
    const next = Object.assign({}, answers);

    if (statusKey === null) {
        itemIds.forEach((id) => delete next[id]);
        return next;
    }

    const allMatch = itemIds.every((id) => next[id] === statusKey);

    itemIds.forEach((id) => {
        if (allMatch) {
            delete next[id];
        } else {
            next[id] = statusKey;
        }
    });

    return next;
}
